import { useEffect, useState } from "react";
import type { Box, ListBox, Read, Result, Blocker } from "@/components/ui/box.schema";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ConflictNotice } from "@/components/ui/conflict-notice";
import { ApiError } from "@/lib/api/client";
import { formatInCourseZone } from "@/lib/session-portal";
import { outcomesApi, type CourseOutcome, type CourseOutcomeProjection } from "@/lib/api/outcomes";
import { recorded, registerCommit, registerCopy, keepResult, registerText, registerPhrases } from "@/lib/register-copy";
import type { Props } from "./CourseOutcomesPanel";

type Draft = {
  version?: string;
  proposalId?: string;
  evidence: string;
  letter: string;
  score: string;
  reason: string;
  impact: string;
};

export function useOutcomeBox({
  mode,
  projection,
  isLoading,
  isError,
  onRetry,
  onRefresh,
  focusEnrollmentId,
  read,
}: Props): Box {
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [results, setResults] = useState<Record<string, Result>>({});
  useEffect(() => {
    if (!focusEnrollmentId) return;
    Array.from(document.querySelectorAll<HTMLElement>('[data-box-id="course-outcomes"] [data-row]'))
      .find((element) => element.dataset.row === focusEnrollmentId)
      ?.querySelector<HTMLElement>(".bento-row-open")
      ?.focus();
  }, [focusEnrollmentId, projection]);
  const readState: Read<CourseOutcomeProjection> =
    read ??
    (isLoading
      ? { status: "loading" }
      : isError
        ? {
            status: "failed",
            sentence:
              registerText.courseOutcomesAreUnavailableNoResultIsShownAsOfficial,
            retry: () => onRetry?.(),
          }
        : projection
          ? !projection.outcomes.length ? { status: "empty", sentence: registerText.noEnrolledLearners } : {
              status: "ready",
              value: projection,
              readAt: new Date().toISOString(),
            }
          : {
              status: "unread",
              sentence: registerText.courseOutcomesHaveNotBeenRead,
            });
  const box: ListBox<CourseOutcomeProjection, CourseOutcome> = {
    kind: "list",
    id: "course-outcomes",
    job: registerText.keepProposalsSeparateFromCertifiedAcademicOutcomes,
    label: mode === "admin" ? registerText.certificationQueue : registerText.outcomeCloseout,
    owner: { system: "imam" },
    read: readState,
    fold: "never",
    detail: () => "",
    state: (value) => ({
      class: value.summary.proposals_awaiting_certification
        ? "waiting"
        : "quiet",
      word: value.summary.proposals_awaiting_certification
        ? registerText.awaitingCertification2
        : registerText.noProposalsAwaitingCertification,
    }),
    figures: (value) => [
      {
        value: value.summary.proposals_awaiting_certification,
        unit: registerText.awaitingCertification,
      },
    ],
    rows: (value) => value.outcomes,
    candidate: { eligible: mode === "admin", rank: null, blocked: null },
    draft: Object.keys(drafts).length > 0,
    row: {
      key: (row) => row.enrollment_id,
      title: (row) => row.display_name,
      state: (row) => ({
        class:
          row.status === "certified" || row.status === "corrected"
            ? "done"
            : row.proposal
              ? "waiting"
              : "quiet",
        word: registerCopy.outcome[row.status],
      }),
      facts: (row) =>
        row.standing_certification
          ? `${row.standing_certification.certified_letter}${row.standing_certification.certified_score == null ? "" : ` · ${row.standing_certification.certified_score}%`}`
          : row.proposal
            ? registerPhrases.provisionalLetter(row.proposal.result_letter)
            : "",
      act: (row) => ({
        does: "open",
        verb:
          mode === "admin" && row.proposal
            ? row.standing_certification
              ? registerText.correct
              : registerText.certify
            : registerText.readOutcome,
      }),
      startsOpen: (row) => row.enrollment_id === focusEnrollmentId,
      detail: (row) => (
        <div className="grid gap-2 text-ui">
          {row.proposal && (
            <>
            <dl className="grid gap-1">
              <div><dt>{registerText.submittedValue}</dt><dd>{row.proposal.result_letter}{row.proposal.result_score == null ? "" : ` · ${row.proposal.result_score}%`}</dd></div>
              <div><dt>{registerText.source}</dt><dd>{registerText.imamGradebook}</dd></div>
              <div><dt>{registerText.submitted}</dt><dd>{formatInCourseZone(row.proposal.proposed_at, projection?.timezone ?? "UTC")}</dd></div>
            </dl>
            </>
          )}
          {mode === "admin" &&
            row.certification_events.map((event) => (
              <p key={event.id}>
                {registerPhrases.eventLabel(event.kind, event.event_no)}
                : {event.certified_letter}
                {event.prior_letter ? registerPhrases.prior(event.prior_letter) : ""}
                {event.reason ? registerPhrases.reason(event.reason) : ""}
                {event.downstream_impact
                  ? registerPhrases.impact(event.downstream_impact)
                  : ""}
                {event.evidence_ref ? registerPhrases.evidence(event.evidence_ref) : ""}
              </p>
            ))}
        </div>
      ),
      task: (row) => {
        if (mode !== "admin" || (!row.proposal && !row.standing_certification))
          return undefined;
        const correcting = Boolean(row.standing_certification);
        const draft = drafts[row.enrollment_id] ?? {
          version: row.updated_at,
          proposalId: row.proposal?.id,
          evidence: "",
          letter: row.standing_certification?.certified_letter ?? "",
          score: row.standing_certification?.certified_score?.toString() ?? "",
          reason: "",
          impact: "",
        };
        const result = results[row.enrollment_id] ?? { state: "none" };
        const conflict = Boolean(drafts[row.enrollment_id] && draft.version !== row.updated_at);
        const set = (field: keyof Draft, value: string) => {
          setDrafts((held) => ({
            ...held,
            [row.enrollment_id]: { ...draft, [field]: value },
          }));
          setResults((held) => ({
            ...held,
            [row.enrollment_id]: { state: "none" },
          }));
        };
        const blockers = (): Blocker[] => {
          if (conflict) return [{ kind: "conflict", part: row.enrollment_id, sentence: registerText.chooseHowToUseTheNewerOutcomeBeforeRecordingAn }];
          if (!row.updated_at)
            return [
              {
                kind: "missing",
                part: row.enrollment_id,
                sentence: registerText.readTheLatestOutcomeBeforeRecordingAnEvent,
              },
            ];
          const missing = !draft.evidence.trim()
            ? registerText.evidenceReferenceIsRequired
            : correcting && !draft.letter.trim()
              ? registerText.correctedLetterIsRequired
              : correcting && !draft.reason.trim()
                ? registerText.reasonIsRequired
                : correcting && !draft.impact.trim()
                  ? registerText.declareTheDownstreamImpact
                  : correcting &&
                      draft.score.trim() &&
                      (!Number.isFinite(Number(draft.score)) ||
                        Number(draft.score) < 0 ||
                        Number(draft.score) > 100)
                    ? registerText.correctedNumericScoreMustBeFrom0To100
                    : null;
          return missing
            ? [{ kind: "missing", part: !draft.evidence.trim() ? `${row.enrollment_id}-evidence` : row.enrollment_id, sentence: missing }]
            : [];
        };
        const field = (name: keyof Draft, label: string, multiline = false) => {
          const id = `${row.enrollment_id}-${name}`;
          return (
            <div className="grid gap-1">
              <Label htmlFor={id}>{label}</Label>
              {multiline ? (
                <Textarea
                  id={id}
                  value={draft[name]}
                  disabled={result.state === "unknown"}
                  onChange={(event) => set(name, event.target.value)}
                />
              ) : (
                <Input
                  id={id}
                  value={draft[name]}
                  disabled={result.state === "unknown"}
                  onChange={(event) => set(name, event.target.value)}
                />
              )}
            </div>
          );
        };
        return {
          result,
          render: () => (
            <div className="grid gap-3" id={row.enrollment_id}>
              {conflict && <ConflictNotice
                object={registerText.courseOutcome}
                message={registerText.thisOutcomeChangedYourEvidenceAndCorrectionDraftAreKept}
                facts={[{ label: registerText.currentProposal, theirs: row.proposal?.result_letter ?? registerText.noProposal }, { label: registerText.currentCertification, theirs: row.standing_certification?.certified_letter ?? registerText.notCertified }]}
                keepMineLabel={registerText.keepMyDraftOnTheNewerOutcome}
                loadTheirsLabel={registerText.loadTheNewerOutcome}
                onKeepMine={() => setDrafts((held) => ({ ...held, [row.enrollment_id]: { ...draft, version: row.updated_at, proposalId: row.proposal?.id } }))}
                onLoadTheirs={() => {
                  setDrafts((held) => { const next = { ...held }; delete next[row.enrollment_id]; return next; });
                  setResults((held) => ({ ...held, [row.enrollment_id]: { state: "none" } }));
                }}
              />}
              {correcting && (
                <>
                  {field("letter", registerText.correctedLetterGrade)}
                  {field("score", registerText.correctedNumericScoreOptional)}
                  {field("reason", registerText.reason, true)}
                  {field("impact", registerText.downstreamImpact, true)}
                </>
              )}
            </div>
          ),
          parts: [{
            id: `${row.enrollment_id}-evidence`, label: registerText.evidence,
            state: { class: draft.evidence.trim() ? "draft" : "waiting", word: draft.evidence.trim() ? registerText.notRecorded : registerText.required },
            owner: { system: "imam" }, fold: "never", draft: "browser",
            render: () => field("evidence", registerText.evidenceReference),
          }],
          commit: {
            verb: correcting ? registerText.recordCorrection : registerText.certifyExactProposal,
            cap: correcting
              ? registerText.whenYouRecordTheCorrection
              : registerText.whenYouCertify,
            effects: () => [
              {
                id: row.enrollment_id,
                sentence: correcting
                  ? registerPhrases.correctionEffect(draft.letter)
                  : registerPhrases.certifyEffect(row.proposal!.result_letter),
              },
            ],
            blockers,
            run: async (key) => {
              const next = await registerCommit(
                key,
                async () => {
                  try { return await (correcting
                    ? outcomesApi.correct(
                        row.enrollment_id,
                        {
                          certified_letter: draft.letter.trim(),
                          certified_score: draft.score.trim()
                            ? Number(draft.score)
                            : null,
                          reason: draft.reason.trim(),
                          downstream_impact: draft.impact.trim(),
                          evidence_ref: draft.evidence.trim(),
                        },
                        { key, version: draft.version! },
                      )
                    : outcomesApi.certify(
                        row.enrollment_id,
                        {
                          proposal_id: draft.proposalId!,
                          evidence_ref: draft.evidence.trim(),
                        },
                        { key, version: draft.version! },
                      )); } catch (error) {
                        if (error instanceof ApiError && error.status === 409) await onRefresh();
                        throw error;
                      }
                },
                async () => {
                  await onRefresh();
                  setDrafts((held) => { const next = { ...held }; delete next[row.enrollment_id]; return next; });
                  return recorded([
                    correcting
                      ? registerText.correctionRecordedPriorCertificationPreserved
                      : registerText.exactProposalCertified,
                  ]);
                },
              );
              return keepResult(next, (value) =>
                setResults((held) => ({ ...held, [row.enrollment_id]: value })),
              );
            },
          },
        };
      },
    },
  };
  return box as Box;
}
